import React, { useCallback, useEffect, useMemo } from 'react';
import { Platform, View } from 'react-native';
import WorkSitePublish from '@/components/Coworker/WorkSitePublish';
import HtmlPlayablePreview from '@/components/Coworker/HtmlPlayablePreview';
import { filesApi } from '@/lib/api/files';
import { openMessageFile } from '@/constants/utils/files';
import { suggestedHtmlFilename } from '@/lib/workSitePublishing';

// A fenced HTML response has no File yet. Create one only when the user opens
// or publishes the artifact, so all actions use the same saved source.
export default function InlineHtmlPreview({ html, conversationId, isFromTemporaryChat = false }) {
  const name = useMemo(() => suggestedHtmlFilename(html), [html]);
  const source = useMemo(() => ({ active: true, file: null }), [html, conversationId, isFromTemporaryChat]);
  useEffect(() => {
    source.active = true;
    return () => { source.active = false; };
  }, [source]);

  const saveArtifact = useCallback(async () => {
    if (!source.file) {
      let file;
      let cleanup;
      try {
        if (Platform.OS === 'web') {
          file = new File([html], name, { type: 'text/html' });
        } else {
          const FileSystem = await import('expo-file-system');
          const uri = `${FileSystem.cacheDirectory}html-artifact-${Date.now()}-${Math.random().toString(36).slice(2)}.html`;
          cleanup = () => FileSystem.deleteAsync(uri, { idempotent: true });
          await FileSystem.writeAsStringAsync(uri, html);
          file = { uri, name, type: 'text/html' };
        }
        const uploaded = await filesApi.uploadFile({ file, name, conversationId, isFromTemporaryChat });
        if (!uploaded?.file?.cdnPath) throw new Error('HTML artifact was not saved');
        source.file = { ...uploaded.file, conversationId };
      } finally {
        if (cleanup) await cleanup().catch(() => {});
      }
    }
    return source.file;
  }, [html, name, conversationId, isFromTemporaryChat, source]);

  // Opening and publishing share the same saved File, even if both surfaces
  // are clicked while its initial upload is still in flight.
  const prepareFile = useCallback(async () => {
    if (!source.active) throw new Error('This preview changed. Open the current creation to publish it.');
    if (!source.pending) source.pending = saveArtifact().finally(() => { source.pending = null; });
    const file = await source.pending;
    if (!source.active) throw new Error('This preview changed. Open the current creation to publish it.');
    return file;
  }, [source, saveArtifact]);
  const openArtifact = useCallback(async () => {
    try {
      const file = await prepareFile();
      if (source.active) openMessageFile(file);
    } catch (error) {
      if (source.active) throw error;
    }
  }, [prepareFile, source]);

  return <View style={{ gap: 8 }}>
    <HtmlPlayablePreview name={name} sourceKey={html} loadHtml={() => html} onExpand={openArtifact} expandLabel="Open artifact" />
    <WorkSitePublish key={`${conversationId || ''}:${isFromTemporaryChat}:${html}`} prepareFile={prepareFile} file={{ name }} />
  </View>;
}
